import { CloseIcon, DeleteIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Button,
  Text,
  useColorMode,
  VStack,
  Center,
} from '@chakra-ui/react'
import { WalletNotConnectedError } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  Transaction,
} from '@solana/web3.js'
import axios from 'axios'
import { Select } from 'chakra-react-select'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useAsyncFn } from 'react-use'
import { z } from 'zod'
import { connection, getBaseUrl } from '../../../config/config'
import {
  handleTransaction,
  handleTransactionError,
} from '../../../utils/solUtils'
import { trpc } from '../../../utils/trpc'
import { useUser } from '../../common/auth/authHooks'
import {
  UseOnChainRaffleType,
  UseOnChainUserRaffleType,
} from '../../techRaffles/hooks/hookTypes'
import { raffleType } from '../../techRaffles/types'
import { ChakraNormalSelectStyle, CharkaSelectStyle } from '../consts/styles'
import { RaffleOnChainInfoBox } from './RaffleOnChainInfoBox'
import { SignMessageToLoginButton } from './SignMessageToLoginButton'

type RaffleHostInfoBoxProps = {
  onChainUserRaffleRes: UseOnChainUserRaffleType
  onChainRaffleRes: UseOnChainRaffleType
  raffle: raffleType
}

export const RaffleHostInfoAndCancelBox: React.FC<RaffleHostInfoBoxProps> = ({
  raffle,
  onChainRaffleRes,
  onChainUserRaffleRes,
}) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const wallet = useWallet()
  const { isAuthed } = useUser()
  const [buttonLoadingText, setButtonLoadingText] = useState('Cancelling...')
  const [cancelledRaffle, setCancelledRaffle] = useState(false)

  const cancelRaffleMut = trpc.useMutation(['raffle.cancelRaffle'], {
    onSuccess: async (data) => {},
    onError: (error) => {
      console.log('error buy ticket request', error)
      toast.error(error.message)
    },
  })

  const [cancelRaffleRes, cancelRaffle] = useAsyncFn(async () => {
    if (!wallet.publicKey || !wallet.signTransaction || !raffle) return

    console.log('buying ticket starting')

    try {
      setButtonLoadingText('Cancelling...')
      const cancelRaffleRes = await cancelRaffleMut.mutateAsync({
        raffleId: raffle.id,
      })

      if (
        cancelRaffleRes.cancelledRaffle.status === 'CANCELLED' &&
        !cancelRaffleRes.trans
      ) {
        toast.success('Cancelled raffle')
        return
      }

      console.log(
        'successful cancel raffle init',
        cancelRaffleRes.cancelledRaffle
      )

      if (
        !wallet.publicKey ||
        !wallet.signTransaction ||
        !cancelRaffleRes.cancelledRaffle
      ) {
        console.log('no wallet or no raffle', wallet.publicKey, cancelRaffleRes)
        return
      }

      const transaction = Transaction.from(
        Buffer.from((cancelRaffleRes as any).trans)
      )
      setButtonLoadingText('Approving...')
      const signedTx = await wallet.signTransaction(transaction)
      setButtonLoadingText('Confirming...')
      const serial = signedTx.serialize({
        verifySignatures: false,
        requireAllSignatures: false,
      })
      const tx = await connection.sendRawTransaction(serial, {skipPreflight: true})

      console.log('handle tx')

      await handleTransaction(tx, {
        blockhash: cancelRaffleRes.blockhash,
        commitment: 'confirmed',
        showLogs: true,
      })

      setButtonLoadingText('Fetching new data...')

      await Promise.all([
        onChainRaffleRes.refetch(),
        onChainUserRaffleRes.refetch(),
      ])

      toast.success(`Cancelled raffle`)
      setCancelledRaffle(true)
    } catch (e: any) {
      console.error('Error in cancelling raffle', e)
      toast.error('Cancelling raffle failed')
      const anchorErrorMessage = handleTransactionError(e)
      if (anchorErrorMessage) {
        toast.error(anchorErrorMessage)
      } else {
        toast.error(e.message)
      }
    }
  }, [wallet, raffle.id])

  if (!wallet.publicKey) return <></>

  return (
    <Box mt='1.5rem'>
      <RaffleOnChainInfoBox
        raffle={raffle}
        onChainRaffleRes={onChainRaffleRes}
      ></RaffleOnChainInfoBox>

      {onChainRaffleRes.data?.ticketCount === 0 &&
        raffle.hostWallet === wallet.publicKey?.toBase58() && (
          <Box
            marginBottom='1rem'
            bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
            borderRadius='1.875rem'
            paddingY='1.5rem'
            paddingX='2.5rem'
          >
            <Text marginBottom='1rem'>
              Raffle hasn't sold any tickets yet so you can still cancel it.{' '}
              {raffle.type === 'NFT'
                ? 'By cancelling, you also claim back your NFT.'
                : ''}
            </Text>
            {cancelledRaffle && (
              <Text>
                Raffle has been cancelled successfully.{' '}
                {raffle.type === 'NFT'
                  ? 'The NFT should be back in your wallet.'
                  : ''}
              </Text>
            )}
            {!cancelledRaffle &&
              (!isAuthed ? (
                <Center mt='15rem'>
                  <VStack
                    maxWidth='500px'
                    display='flex'
                    flexDirection='column'
                    gap='10'
                  >
                    <Text textAlign='center'>
                      To cancel the raffle, please login first by signing a
                      message with your wallet.
                    </Text>
                    <SignMessageToLoginButton />
                  </VStack>
                </Center>
              ) : (
                <Button
                  leftIcon={
                    <CloseIcon color={isDarkMode ? 'black' : 'white'} />
                  }
                  variant={isDarkMode ? 'primaryDark' : 'primary'}
                  loadingText={buttonLoadingText}
                  isLoading={cancelRaffleRes.loading}
                  height={['2.8rem', '3.4rem']}
                  fontSize={['0.8rem', '.9em']}
                  onClick={() => {
                    cancelRaffle()
                  }}
                >
                  Cancel raffle{' '}
                  {raffle.type === 'NFT' ? ' and claim back NFT.' : ''}
                </Button>
              ))}
          </Box>
        )}
    </Box>
  )
}

export default RaffleHostInfoAndCancelBox
